import { useMemo } from 'react';

import { ReactComponent as SpinnerIcon } from '../../../assets/icons/icon_spinner.svg';
import { ReactComponent as ChangePasswordRequestSucceed } from '../../../assets/images/action-result/update_email_succeed.svg';
import { ChangePasswordModal } from '../ChangePasswordModal.container';

import {
  Box,
  Button,
  Form,
  FormItem,
  Icon,
  Spacer,
  TextFieldInput,
  Typography,
} from '@aircall/tractor';
import { Banner } from '@components/Banner';
import { SaveButton, SaveButtonStatus } from '@components/Button/SaveButton';
import { GridItem, GridLayout } from '@components/GridLayout/GridLayout';
import { ModalStatus } from '@components/ModalStatus';
import { Paper } from '@components/Paper/Paper';
import { TextField } from '@components/TextField/TextField';
import { CRITICAL_ACTION } from '@constants/critical-action.constants';
import { ILLUSTRATION_BACKGROUND_COLORS } from '@constants/style.constants';
import { validateEmail } from '@dashboard/library';
import { UserFullFragment } from '@generated/UserFullFragment';
import { useChangePasswordRequest } from '@hooks/useChangePasswordRequest';
import { useResendChangeEmailConfirmation } from '@hooks/useResendChangeEmailConfirmation';
import { useResendCriticalAction } from '@hooks/useResendCriticalAction';
import { useRouteChangePrompt } from '@hooks/useRouteChangePrompt';
import { useSaveStatusHook } from '@hooks/useSaveStatusHook/useSaveStatusHook';
import { useToggle } from '@hooks/useToggle/useToggle';
import { useUpdateEmail } from '@hooks/useUpdateEmail';
import { useUpdateUserInformation } from '@hooks/useUpdateUserInformation';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { Form as RRForm, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';

interface AccountCredentialsSectionProps {
  user: UserFullFragment;
  isCurrentUser: boolean;
  shouldHidePasswordUpdate: boolean;
  fraudPasswordChangeValidation: boolean;
  fraudEmailChangeValidation: boolean;
  onGoingUpdateEmailCriticalAction: boolean;
}

interface AccountCredentialsFormProps extends AccountCredentialsSectionProps {
  formRenderProps: FormRenderProps<UserFullFragment, Partial<UserFullFragment>>;
}

const SaveButtonGridContainer = motion(Box);
const animationVariants: Variants = {
  enter: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeInOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.35,
      ease: 'easeInOut',
    },
  },
};

export const getStatus = (
  {
    submitting,
    submitSucceeded,
    form,
  }: FormRenderProps<UserFullFragment, Partial<UserFullFragment>>,
  reset?: boolean
) => {
  if (submitting) {
    return SaveButtonStatus.SAVING;
  }
  if (submitSucceeded) {
    if (reset) {
      form.reset();
    }
    return SaveButtonStatus.SAVED;
  }
  return SaveButtonStatus.UNSAVED;
};

export const getShouldShowSaveButton = (
  {
    pristine,
    submitSucceeded,
    submitFailed,
  }: FormRenderProps<UserFullFragment, Partial<UserFullFragment>>,
  isSaving: boolean,
  visibleAfterSaved: boolean
) => !pristine || isSaving || (submitSucceeded && visibleAfterSaved) || submitFailed;

function AccountCredentialsForm({
  user,
  isCurrentUser,
  onGoingUpdateEmailCriticalAction,
  shouldHidePasswordUpdate,
  fraudEmailChangeValidation,
  fraudPasswordChangeValidation,
  formRenderProps,
}: AccountCredentialsFormProps) {
  const { t } = useTranslation();
  const { ID, email } = user;

  const { resendCriticalAction, loading: sendingCriticalAction } = useResendCriticalAction(
    CRITICAL_ACTION.UPDATE_EMAIL,
    ID,
    email
  );
  const { sendEmail, loading: sendingEmail } = useResendChangeEmailConfirmation();

  const { changePasswordRequest, loading: sendingChangePasswordRequest } =
    useChangePasswordRequest(email);
  const [isModalOpen, toggleModal] = useToggle();

  const changePasssword = async () => {
    if (fraudPasswordChangeValidation) {
      const { data } = await changePasswordRequest();
      if (!data) {
        return;
      }
    }
    toggleModal();
  };

  const {
    hasValidationErrors,
    handleSubmit: onSubmit,
    pristine,
    hasSubmitErrors,
  } = formRenderProps;

  useRouteChangePrompt(!pristine);

  const savingStatus = useMemo(
    () => getStatus(formRenderProps, fraudEmailChangeValidation),
    [formRenderProps, fraudEmailChangeValidation]
  );

  const visibleAfterSaved = useSaveStatusHook(savingStatus === SaveButtonStatus.SAVED);
  const isSaving = savingStatus === SaveButtonStatus.SAVING;

  const shouldShowSaveButton = useMemo(
    () => getShouldShowSaveButton(formRenderProps, isSaving, visibleAfterSaved),
    [formRenderProps, isSaving, visibleAfterSaved]
  );

  return (
    <Form onSubmit={onSubmit}>
      <Paper fluid title={t('user_profile.general.account_credentials.title')}>
        {(onGoingUpdateEmailCriticalAction || user.unconfirmedEmail) && (
          <Banner
            round
            marginBottom={24}
            type='warning'
            data-test='email-confirmation-banner'
            text={t(
              `user_profile.confirmation_banner.${isCurrentUser ? 'me' : 'other'}.${
                user.unconfirmedEmail ? 'new' : 'current'
              }_email`,
              {
                email: user.unconfirmedEmail ?? user.email,
              }
            )}
          >
            <Button
              mode='link'
              variant='black'
              size='xSmall'
              disabled={sendingEmail || sendingCriticalAction}
              onClick={user.unconfirmedEmail ? sendEmail : resendCriticalAction}
              data-test='email-confirmation-banner-button'
            >
              <Typography
                variant='body'
                color='text.base'
                data-test='email-confirmation-banner-button-text'
              >
                {t('user_profile.confirmation_banner.link')}
              </Typography>
            </Button>
          </Banner>
        )}
        <GridLayout gridColumnGap={{ _: 'm', md: 'l' }} gridRowGap='m'>
          <GridItem gridRow='1' xs={12} md={6} lg={4}>
            <TextField
              name='email'
              validate={validateEmail}
              label={t('app.form.email.label')}
              size='regular'
              data-test='input-email'
              validateInitial
            />
          </GridItem>
          <AnimatePresence>
            {shouldShowSaveButton && (
              <GridItem
                data-test='save-button-item'
                alignSelf={hasValidationErrors || hasSubmitErrors ? 'center' : 'end'}
                margin={hasValidationErrors || hasSubmitErrors ? '5px 0 0' : '0'}
                gridRow={{ xs: 2, md: 1 }}
                xs={12}
                md={6}
              >
                <SaveButtonGridContainer
                  variants={animationVariants}
                  initial='exit'
                  animate='enter'
                  exit='exit'
                  width={182}
                >
                  <SaveButton
                    block
                    status={savingStatus}
                    onClick={onSubmit}
                    disabled={hasValidationErrors}
                    data-test='save-button'
                  />
                </SaveButtonGridContainer>
              </GridItem>
            )}
          </AnimatePresence>

          {isCurrentUser && !shouldHidePasswordUpdate && (
            <>
              <GridItem gridRow={{ xs: 3, md: 2 }} xs={12} md={6} lg={4}>
                <FormItem label={t('app.form.password.label')} name='password'>
                  <TextFieldInput
                    name='password'
                    type='password'
                    size='regular'
                    value='password'
                    disabled
                    data-test='user-profile-input-password'
                  />
                </FormItem>
              </GridItem>
              <GridItem alignSelf='end' gridRow={{ xs: 4, md: 2 }} xs={12} md={6}>
                <Box width={182}>
                  <Button
                    block
                    mode='outline'
                    onClick={changePasssword}
                    disabled={sendingChangePasswordRequest}
                    data-test='change-password-button'
                  >
                    {sendingChangePasswordRequest ? (
                      <Icon
                        spin
                        component={SpinnerIcon}
                        data-test='change-password-button-loading'
                      />
                    ) : (
                      t('user_profile.general.change_password.button')
                    )}
                  </Button>
                </Box>
              </GridItem>
              {fraudPasswordChangeValidation ? (
                <ModalStatus
                  isModalOpen={isModalOpen}
                  onModalHide={toggleModal}
                  title={t('change_password.result.title')}
                  body={t('change_password.result.body', { email })}
                  image={<ChangePasswordRequestSucceed />}
                  imageBackgroundColor={ILLUSTRATION_BACKGROUND_COLORS.LIGHT_GREEN}
                  data-test='change-password-result-modal'
                />
              ) : (
                <ChangePasswordModal
                  isModalOpen={isModalOpen}
                  onModalHide={toggleModal}
                  data-test='change-password-modal'
                />
              )}
            </>
          )}
        </GridLayout>
      </Paper>
    </Form>
  );
}

export function AccountCredentialsSection({
  user,
  isCurrentUser,
  shouldHidePasswordUpdate,
  fraudEmailChangeValidation,
  fraudPasswordChangeValidation,
  onGoingUpdateEmailCriticalAction,
}: AccountCredentialsSectionProps) {
  const { updateUser } = useUpdateUserInformation(user, isCurrentUser);
  const { updateEmail } = useUpdateEmail();

  const criticalActionUpdateEmailFlow =
    user.migratedOnAuthenticationManager && fraudEmailChangeValidation;

  return (
    <Spacer fluid direction='vertical' space='m' data-test='account-credentials-section'>
      <RRForm<UserFullFragment>
        onSubmit={criticalActionUpdateEmailFlow ? updateEmail : updateUser}
        initialValues={user}
      >
        {(formRenderProps) => (
          <AccountCredentialsForm
            user={user}
            isCurrentUser={isCurrentUser}
            onGoingUpdateEmailCriticalAction={onGoingUpdateEmailCriticalAction}
            shouldHidePasswordUpdate={shouldHidePasswordUpdate}
            fraudPasswordChangeValidation={fraudPasswordChangeValidation}
            fraudEmailChangeValidation={!!criticalActionUpdateEmailFlow}
            formRenderProps={formRenderProps}
          />
        )}
      </RRForm>
    </Spacer>
  );
}
